import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from 'react'
import { useAuth } from '@context/AuthContext'
import type { OtpChannel, OtpVerificationStatus, SecurityPortalKey } from '@/types'
import './OtpSecuritySettings.css'

interface CopyConfig {
  heading: string
  description: string
  otpDescription: string
}

const copyByPortal: Partial<Record<SecurityPortalKey, CopyConfig>> = {
  retail: {
    heading: 'Email verification',
    description: 'Protect your account with a verified email address for password resets and order updates.',
    otpDescription: 'We will send a short-lived verification code to your email. Enter it below to complete verification.'
  },
  seller: {
    heading: 'Seller contact verification',
    description: 'Verified contact details are required for payouts, onboarding updates, and OTP sign-in.',
    otpDescription: 'Send a verification code to your seller email address and enter it below to confirm.'
  },
  delivery: {
    heading: 'Delivery email verification',
    description: 'Verified email keeps dispatch alerts and OTP sign-in working on every device.',
    otpDescription: 'Send a verification code to your delivery email address and confirm it below.'
  },
  operations: {
    heading: 'Operations email verification',
    description: 'Verified email ensures workflow escalations and approval alerts reach you instantly.',
    otpDescription: 'We will send a verification code to your email. Enter it below to verify.'
  },
  admin: {
    heading: 'Admin email verification',
    description: 'Verified email is mandatory for compliance exports and elevated admin privileges.',
    otpDescription: 'Send a verification code to your admin email and enter it below to confirm.'
  }
}

interface OtpSecuritySettingsProps {
  portal: SecurityPortalKey
  className?: string
  hideWhenVerified?: boolean
}

const STATUS_LABEL: Record<OtpVerificationStatus | 'UNKNOWN', string> = {
  VERIFIED: 'Verified',
  PENDING: 'Pending',
  UNVERIFIED: 'Not verified',
  UNKNOWN: 'Unknown'
}

function formatStatus(value: OtpVerificationStatus | null | undefined): string {
  if (!value) return STATUS_LABEL.UNKNOWN
  return STATUS_LABEL[value]
}

export function OtpSecuritySettings({ portal, className, hideWhenVerified = true }: OtpSecuritySettingsProps) {
  const { authState, refreshOtpStatus, challengeOtpContact, confirmOtpContact } = useAuth()
  const [hydrated, setHydrated] = useState(Boolean(authState.otpEmailStatus))
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [challengeId, setChallengeId] = useState<string | null>(null)
  const [challengeChannel, setChallengeChannel] = useState<OtpChannel | null>(null)
  const [selectedChannel, setSelectedChannel] = useState<OtpChannel>('EMAIL')
  const [otpCode, setOtpCode] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!authState.isAuthenticated || hydrated) {
      return
    }
    setIsRefreshing(true)
    refreshOtpStatus()
      .catch(() => {
        setError('Unable to load verification status. Try refreshing later.')
      })
      .finally(() => {
        setIsRefreshing(false)
        setHydrated(true)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.isAuthenticated, hydrated])

  const emailStatus = authState.otpEmailStatus ?? null
  const smsStatus = authState.otpSmsStatus ?? null
  const enrolled = authState.otpEnrolled ?? false
  const rawChannels = authState.otpChannels

  const channels = useMemo<OtpChannel[]>(() => {
    const fromState = rawChannels ?? []
    const normalized = Array.from(new Set(fromState)) as OtpChannel[]
    if (normalized.length > 0) {
      return normalized
    }
    if (emailStatus) {
      return ['EMAIL']
    }
    return []
  }, [rawChannels, emailStatus])

  useEffect(() => {
    if (!channels.length) {
      return
    }
    setSelectedChannel(prev => {
      if (channels.includes(prev)) {
        return prev
      }
      if (channels.includes('EMAIL')) {
        return 'EMAIL'
      }
      return channels[0]
    })
  }, [channels])

  const copy = useMemo(() => copyByPortal[portal] ?? copyByPortal.retail!, [portal])

  const allChannelsVerified = channels.length > 0 && channels.every(ch => {
    if (ch === 'SMS') {
      return smsStatus === 'VERIFIED'
    }
    return emailStatus === 'VERIFIED'
  })

  const noChannels = channels.length === 0

  const shouldHide = !authState.isAuthenticated || (hideWhenVerified && allChannelsVerified && !noChannels)
  if (shouldHide) {
    return null
  }

  const channelDescription = (ch: OtpChannel): string => {
    if (ch === 'SMS') {
      return 'Enables OTP delivery via text message and dispatch alerts.'
    }
    return 'Required for password resets, order updates, and account notifications.'
  }

  const channelFriendly = (ch: OtpChannel | null): string => {
    if (ch === 'SMS') {
      return 'SMS'
    }
    return 'email'
  }

  const channelHint = (ch: OtpChannel | null): string => {
    if (ch === 'SMS') {
      const phone = authState.user?.phoneNumber ?? authState.user?.phone
      return phone ? `Code sent via SMS to ${phone}.` : 'Code sent via SMS to the phone number associated with your account.'
    }
    if (authState.user?.email) {
      return `Code sent to ${authState.user.email}.`
    }
    return 'Code sent to your email address on file.'
  }

  const activeChannel = challengeChannel ?? selectedChannel
  const otpDescription = activeChannel === 'SMS'
    ? 'Enter the SMS code you received to verify your phone.'
    : copy.otpDescription

  const handleSend = async () => {
    if (noChannels) {
      setMessage('No verification channels are configured. Please contact support to enable OTP.');
      return;
    }
    if (allChannelsVerified) {
      setMessage('All channels are already verified. No action needed.')
      return
    }
    if (selectedChannel === 'EMAIL' && emailStatus === 'VERIFIED') {
      setMessage('Email is already verified.')
      return
    }
    if (selectedChannel === 'SMS' && smsStatus === 'VERIFIED') {
      setMessage('SMS is already verified.')
      return
    }
    setError(null)
    setMessage(null)
    setIsSending(true)
    try {
      const response = await challengeOtpContact(selectedChannel, { origin: `${portal}_portal_security` })
      const responseChannel = (response.channel ?? selectedChannel) as OtpChannel
      setChallengeId(response.challengeId)
      setChallengeChannel(responseChannel)
      setOtpCode('')
      setMessage(`Verification code sent via ${channelFriendly(responseChannel)}. Enter the code below to complete verification.`)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unable to send verification code'
      setError(msg)
    } finally {
      setIsSending(false)
    }
  }

  const handleVerify = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!challengeId) {
      setError('Verification challenge expired. Send a new code to continue.')
      return
    }
    setIsVerifying(true)
    setError(null)
    setMessage(null)
    try {
      await confirmOtpContact(challengeId, otpCode.trim())
      setChallengeId(null)
      setChallengeChannel(null)
      setOtpCode('')
      setMessage(`${channelFriendly(activeChannel)} verification complete.`)
      await refreshOtpStatus()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Verification failed. Check the code and try again.'
      setError(msg)
    } finally {
      setIsVerifying(false)
    }
  }

  const handleCancel = () => {
    setChallengeId(null)
    setChallengeChannel(null)
    setOtpCode('')
    setError(null)
    setMessage(null)
  }

  const handleRefresh = async () => {
    setIsRefreshing(true)
    setError(null)
    setMessage(null)
    try {
      await refreshOtpStatus()
      setMessage('Verification status refreshed.')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unable to refresh verification status'
      setError(msg)
    } finally {
      setIsRefreshing(false)
    }
  }

  const statusClass = (value: OtpVerificationStatus | null | undefined) => {
    switch (value) {
      case 'VERIFIED':
        return 'otp-security__badge otp-security__badge--success'
      case 'PENDING':
        return 'otp-security__badge otp-security__badge--pending'
      default:
        return 'otp-security__badge otp-security__badge--warning'
    }
  }

  return (
    <section className={['otp-security', className].filter(Boolean).join(' ')} aria-live="polite">
      <header className="otp-security__header">
        <div>
          <h2>{copy.heading}</h2>
          <p>{copy.description}</p>
        </div>
        <div className="otp-security__actions">
          {channels.length > 1 && (
            <label className="otp-security__channel">
              <span>Channel</span>
              <select
                value={selectedChannel}
                onChange={(event: ChangeEvent<HTMLSelectElement>) => setSelectedChannel(event.target.value as OtpChannel)}
                disabled={isSending || isVerifying || Boolean(challengeId) || noChannels}
              >
                {channels.map(ch => (
                  <option key={ch} value={ch}>
                    {ch === 'SMS' ? 'SMS' : 'Email'}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button type="button" className="otp-security__button secondary" onClick={handleRefresh} disabled={isRefreshing}>
            {isRefreshing ? 'Refreshing…' : 'Refresh status'}
          </button>
          <button type="button" className="otp-security__button primary" onClick={handleSend} disabled={isSending || noChannels}>
            {challengeId ? 'Resend code' : 'Send code'}
          </button>
        </div>
      </header>

      {error && <div className="otp-security__message otp-security__message--error">{error}</div>}
      {message && <div className="otp-security__message otp-security__message--success">{message}</div>}

      {noChannels && (
        <div className="otp-security__message otp-security__message--info">
          No OTP channels are currently configured for this account. Please contact your administrator or support team to enable verification.
        </div>
      )}

      <div className="otp-security__status-grid">
        <div className="otp-security__status">
          <span className="otp-security__status-label">OTP enrollment</span>
          <span className={statusClass(enrolled ? 'VERIFIED' : 'UNVERIFIED')}>
            {enrolled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
        {channels.map(ch => {
          const status = ch === 'SMS' ? smsStatus : emailStatus
          return (
            <div className="otp-security__status" key={ch}>
              <div>
                <span className="otp-security__status-label">
                  {ch === 'SMS' ? 'SMS verification' : 'Email verification'}
                </span>
                <span className="otp-security__status-description">{channelDescription(ch)}</span>
              </div>
              <span className={statusClass(status)}>{formatStatus(status)}</span>
            </div>
          )
        })}
      </div>

      {challengeId && (
        <form className="otp-security__form" onSubmit={handleVerify}>
          <label htmlFor="otp-security-code">{otpDescription}</label>
          <div className="otp-security__form-row">
            <input
              id="otp-security-code"
              type="text"
              value={otpCode}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setOtpCode(event.target.value)}
              placeholder="123456"
              inputMode="numeric"
              autoComplete="one-time-code"
              disabled={isVerifying}
              required
            />
            <div className="otp-security__form-actions">
              <button type="submit" className="otp-security__button primary" disabled={isVerifying || otpCode.trim().length === 0}>
                {isVerifying ? 'Verifying…' : 'Verify'}
              </button>
              <button type="button" className="otp-security__button secondary" onClick={handleCancel} disabled={isVerifying}>
                Cancel
              </button>
            </div>
          </div>
          <span className="otp-security__hint">
            {channelHint(activeChannel)}
          </span>
        </form>
      )}
    </section>
  )
}

export default OtpSecuritySettings
